import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
    ChevronDown, ChevronRight, Save, Image as ImageIcon, Plus, Trash2,
    GripVertical, Upload, Copy, RefreshCw, Sparkles, Settings as SettingsIcon, Layers,
} from "lucide-react";
import { api } from "@/lib/api";
import { useCms } from "@/context/CmsContext";
import { cn } from "@/lib/utils";

// ---------- Common helpers ----------
function fieldKindInputProps(kind) {
    if (kind === "email") return { type: "email" };
    if (kind === "url") return { type: "url" };
    if (kind === "phone") return { type: "tel" };
    if (kind === "number") return { type: "number" };
    return { type: "text" };
}

const inputBase =
    "w-full rounded-md border border-border bg-white px-3 py-2 text-sm text-primary outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/10";

// ---------- Image picker (picks from media library or uploads) ----------
function ImagePicker({ value, onChange, label }) {
    const [open, setOpen] = useState(false);
    const [uploads, setUploads] = useState([]);
    const [loading, setLoading] = useState(false);
    const fileInput = useRef(null);

    const fetchUploads = useCallback(async () => {
        try {
            const res = await api.get("/admin/cms/uploads");
            setUploads(res.data || []);
        } catch (e) {
            console.warn("uploads fetch failed", e);
        }
    }, []);

    useEffect(() => {
        if (open) fetchUploads();
    }, [open, fetchUploads]);

    async function handleUpload(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setLoading(true);
        try {
            const res = await api.post("/admin/cms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Imagem carregada");
            onChange(res.data.url);
            await fetchUploads();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Falha no upload");
        } finally {
            setLoading(false);
            e.target.value = "";
        }
    }

    return (
        <div>
            {label && <label className="block text-xs font-semibold text-primary/80 mb-1.5">{label}</label>}
            <div className="flex gap-2 items-stretch">
                <input
                    {...fieldKindInputProps("url")}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="/uploads/... ou URL externa"
                    className={inputBase + " flex-1"}
                />
                <button
                    type="button"
                    onClick={() => fileInput.current?.click()}
                    className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-xs font-bold text-white hover:bg-primary/90"
                    disabled={loading}
                    data-testid="image-upload-btn"
                >
                    <Upload className="h-3.5 w-3.5" />
                    {loading ? "..." : "Upload"}
                </button>
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="inline-flex items-center gap-1 rounded-md border border-border bg-white px-3 py-2 text-xs font-bold text-primary hover:bg-muted"
                >
                    <ImageIcon className="h-3.5 w-3.5" /> Galeria
                </button>
                <input ref={fileInput} type="file" accept="image/*" hidden onChange={handleUpload} />
            </div>
            {value && (
                <div className="mt-2 rounded-md border border-border bg-muted/40 p-2">
                    <img src={value} alt="" className="h-16 w-full object-contain rounded" onError={(e) => { e.target.style.display = "none"; }} />
                </div>
            )}

            {open && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
                    <div className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-heading text-lg font-bold">Galeria de imagens</h3>
                            <button onClick={() => setOpen(false)} className="text-sm text-muted-foreground hover:text-primary">Fechar</button>
                        </div>
                        {uploads.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-12 text-center">Sem imagens carregadas. Use o botão &ldquo;Upload&rdquo;.</p>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                {uploads.map((u) => (
                                    <button key={u.filename} type="button"
                                        onClick={() => { onChange(u.url); setOpen(false); }}
                                        className="rounded-md border border-border overflow-hidden hover:border-primary hover:ring-2 hover:ring-primary/20 transition group">
                                        <img src={u.url} alt={u.original_name} className="h-32 w-full object-cover" />
                                        <div className="p-2 text-[10px] text-muted-foreground truncate">{u.filename}</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------- Generic field input (renders correct input by kind) ----------
function FieldInput({ kind, value, onChange, label, options }) {
    if (kind === "image") {
        return <ImagePicker value={value} onChange={onChange} label={label} />;
    }
    if (kind === "textarea" || kind === "richtext") {
        return (
            <div>
                {label && <label className="block text-xs font-semibold text-primary/80 mb-1.5">{label}</label>}
                <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className={inputBase + " min-h-[80px] resize-y"} />
            </div>
        );
    }
    if (kind === "color") {
        return (
            <div>
                {label && <label className="block text-xs font-semibold text-primary/80 mb-1.5">{label}</label>}
                <div className="flex gap-2">
                    <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 rounded border border-border" />
                    <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="#000000" className={inputBase + " flex-1 font-mono text-xs"} />
                </div>
            </div>
        );
    }
    if (kind === "select" && options) {
        return (
            <div>
                {label && <label className="block text-xs font-semibold text-primary/80 mb-1.5">{label}</label>}
                <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputBase}>
                    {options.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>
        );
    }
    return (
        <div>
            {label && <label className="block text-xs font-semibold text-primary/80 mb-1.5">{label}</label>}
            <input
                {...fieldKindInputProps(kind)}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                className={inputBase}
            />
        </div>
    );
}

// ---------- List item editor (repeater) ----------
function ListEditor({ list, schema, onChange }) {
    const updateItem = (index, key, val) => {
        const next = list.map((it, i) => (i === index ? { ...it, [key]: val } : it));
        onChange(next);
    };
    const addItem = () => {
        const empty = {};
        schema.forEach((f) => {
            empty[f.name] = f.kind === "number" ? 0 : "";
        });
        onChange([...list, empty]);
    };
    const removeItem = (index) => onChange(list.filter((_, i) => i !== index));
    const moveItem = (from, to) => {
        if (to < 0 || to >= list.length) return;
        const arr = [...list];
        const [it] = arr.splice(from, 1);
        arr.splice(to, 0, it);
        onChange(arr);
    };

    return (
        <div className="space-y-3">
            {list.map((item, idx) => (
                <div key={idx} className="rounded-lg border border-border bg-muted/30 p-4" data-testid={`list-item-${idx}`}>
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <GripVertical className="h-4 w-4 text-muted-foreground" />
                            <span className="text-xs font-bold text-primary/70">#{idx + 1}</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button type="button" onClick={() => moveItem(idx, idx - 1)} className="px-2 py-1 text-xs hover:bg-muted rounded">↑</button>
                            <button type="button" onClick={() => moveItem(idx, idx + 1)} className="px-2 py-1 text-xs hover:bg-muted rounded">↓</button>
                            <button type="button" onClick={() => removeItem(idx)} className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded">
                                <Trash2 className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        {schema.map((f) => (
                            <FieldInput
                                key={f.name}
                                kind={f.kind}
                                label={f.label}
                                value={item[f.name]}
                                onChange={(v) => updateItem(idx, f.name, v)}
                            />
                        ))}
                    </div>
                </div>
            ))}
            <button type="button" onClick={addItem} data-testid="list-add-item"
                className="w-full inline-flex items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-white px-4 py-3 text-sm font-semibold text-primary hover:border-primary hover:bg-muted transition">
                <Plus className="h-4 w-4" /> Adicionar item
            </button>
        </div>
    );
}

// ============================================================================
// Main page
// ============================================================================
export default function AdminContent() {
    const { refresh: cmsRefresh } = useCms();
    const [registry, setRegistry] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activePage, setActivePage] = useState("home");
    const [section, setSection] = useState({});      // { section: { key: value } }
    const [openSection, setOpenSection] = useState(null);
    const [savingKey, setSavingKey] = useState(null);
    const [activeList, setActiveList] = useState(null);
    const [listItems, setListItems] = useState([]);

    // Settings
    const [settings, setSettings] = useState({});
    const [showSettings, setShowSettings] = useState(false);

    const fetchRegistry = useCallback(async () => {
        const res = await api.get("/admin/cms/registry");
        setRegistry(res.data);
    }, []);

    const fetchPage = useCallback(async (pageId) => {
        const res = await api.get(`/admin/cms/page/${pageId}`);
        setSection(res.data || {});
    }, []);

    const fetchSettings = useCallback(async () => {
        const res = await api.get("/admin/cms/settings");
        const map = {};
        (res.data || []).forEach((s) => { map[s.key] = s; });
        setSettings(map);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                await fetchRegistry();
                await fetchPage(activePage);
                await fetchSettings();
            } finally { setLoading(false); }
        })();
    }, [fetchRegistry, fetchSettings]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!loading) fetchPage(activePage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePage]);

    async function saveField(sectionKey, fieldKey, value) {
        setSavingKey(`${sectionKey}.${fieldKey}`);
        try {
            await api.put(`/admin/cms/page/${activePage}/section/${sectionKey}/field/${fieldKey}`, { value });
            toast.success("Guardado", { duration: 1200 });
            setSection((prev) => ({ ...prev, [sectionKey]: { ...(prev[sectionKey] || {}), [fieldKey]: value } }));
            cmsRefresh();
        } catch (err) {
            toast.error("Falha ao guardar");
        } finally { setSavingKey(null); }
    }

    async function saveSetting(key, value) {
        setSavingKey(`setting.${key}`);
        try {
            await api.put(`/admin/cms/setting/${key}`, { value });
            toast.success("Guardado", { duration: 1200 });
            setSettings((prev) => ({ ...prev, [key]: { ...prev[key], value } }));
            cmsRefresh();
        } catch (err) {
            toast.error("Falha ao guardar");
        } finally { setSavingKey(null); }
    }

    async function openList(listId) {
        setActiveList(listId);
        const res = await api.get(`/admin/cms/list/${listId}`);
        setListItems(res.data?.items || []);
    }

    async function saveList() {
        setSavingKey("list");
        try {
            await api.put(`/admin/cms/list/${activeList}`, { items: listItems });
            toast.success("Lista guardada");
            cmsRefresh();
        } catch (err) {
            toast.error("Falha ao guardar lista");
        } finally { setSavingKey(null); }
    }

    const settingsByGroup = useMemo(() => {
        const grouped = {};
        Object.entries(registry?.settings || {}).forEach(([k, conf]) => {
            const grp = conf.group || "geral";
            grouped[grp] = grouped[grp] || [];
            grouped[grp].push([k, conf]);
        });
        return grouped;
    }, [registry]);

    if (loading || !registry) {
        return <div className="text-sm text-muted-foreground">A carregar...</div>;
    }

    const pageDef = registry.pages[activePage];
    const pageLists = Object.entries(registry.lists).filter(([, v]) => v.page_id === activePage);

    return (
        <div className="space-y-6" data-testid="admin-content-page">
            {/* HEADER */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl lg:text-3xl font-extrabold">Conteúdo do site</h2>
                    <p className="text-sm text-muted-foreground mt-1">Edite textos, imagens e listas de cada página. Tudo é guardado automaticamente.</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setShowSettings((v) => !v)} data-testid="toggle-settings"
                        className={cn("inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-bold transition",
                            showSettings ? "bg-primary text-white border-primary" : "border-border bg-white text-primary hover:bg-muted")}>
                        <SettingsIcon className="h-3.5 w-3.5" /> Definições globais
                    </button>
                    <button onClick={() => { fetchPage(activePage); cmsRefresh(); toast.success("Recarregado"); }}
                        className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-sm font-bold text-primary hover:bg-muted">
                        <RefreshCw className="h-3.5 w-3.5" /> Recarregar
                    </button>
                </div>
            </div>

            {/* GLOBAL SETTINGS */}
            {showSettings && (
                <div className="rounded-xl border border-border bg-white p-5 lg:p-6" data-testid="global-settings-panel">
                    <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2"><SettingsIcon className="h-4 w-4 text-secondary" /> Definições globais</h3>
                    <div className="space-y-6">
                        {Object.entries(settingsByGroup).map(([group, items]) => (
                            <div key={group}>
                                <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-primary/60 mb-3">{group}</h4>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    {items.map(([key, conf]) => (
                                        <SettingRow key={key} k={key} conf={conf} value={settings[key]?.value || ""}
                                            onSave={(v) => saveSetting(key, v)} saving={savingKey === `setting.${key}`} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* PAGE SELECTOR */}
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2" data-testid="page-tabs">
                {Object.entries(registry.pages).map(([pid, p]) => (
                    <button key={pid} onClick={() => { setActivePage(pid); setOpenSection(null); setActiveList(null); }}
                        data-testid={`page-tab-${pid}`}
                        className={cn(
                            "inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-sm font-bold whitespace-nowrap transition",
                            activePage === pid
                                ? "bg-primary text-white shadow-sm"
                                : "bg-white border border-border text-primary hover:bg-muted"
                        )}>
                        {p.label}
                    </button>
                ))}
            </div>

            {/* SECTIONS (flat fields) */}
            <div className="rounded-xl border border-border bg-white">
                <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                    <h3 className="font-heading text-lg font-bold flex items-center gap-2"><Sparkles className="h-4 w-4 text-secondary" /> Secções da página</h3>
                    <span className="text-xs text-muted-foreground">{Object.keys(pageDef.sections).length} secções</span>
                </div>
                <div className="divide-y divide-border">
                    {Object.entries(pageDef.sections).map(([secKey, secDef]) => {
                        const isOpen = openSection === secKey;
                        return (
                            <div key={secKey} data-testid={`section-${secKey}`}>
                                <button onClick={() => setOpenSection(isOpen ? null : secKey)} className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-muted/40 transition">
                                    <div className="flex items-center gap-2 text-left">
                                        {isOpen ? <ChevronDown className="h-4 w-4 text-primary" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                                        <div>
                                            <div className="font-semibold text-sm text-primary">{secDef.label}</div>
                                            <div className="text-[11px] text-muted-foreground mt-0.5">{Object.keys(secDef.fields).length} campos</div>
                                        </div>
                                    </div>
                                </button>
                                {isOpen && (
                                    <div className="px-5 pb-5 grid sm:grid-cols-2 gap-4 bg-muted/20">
                                        {Object.entries(secDef.fields).map(([fkey, fconf]) => (
                                            <FieldRow key={fkey} f={fconf} value={section[secKey]?.[fkey] || ""}
                                                onSave={(v) => saveField(secKey, fkey, v)} saving={savingKey === `${secKey}.${fkey}`} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* LISTS */}
            {pageLists.length > 0 && (
                <div className="rounded-xl border border-border bg-white">
                    <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                        <h3 className="font-heading text-lg font-bold flex items-center gap-2"><Layers className="h-4 w-4 text-secondary" /> Listas da página</h3>
                    </div>
                    <div className="divide-y divide-border">
                        {pageLists.map(([lid, ldef]) => (
                            <div key={lid} data-testid={`list-${lid}`}>
                                <button onClick={() => activeList === lid ? setActiveList(null) : openList(lid)}
                                    className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-muted/40 transition">
                                    <div className="flex items-center gap-2 text-left">
                                        {activeList === lid ? <ChevronDown className="h-4 w-4 text-primary" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                                        <div className="font-semibold text-sm text-primary">{ldef.label}</div>
                                    </div>
                                </button>
                                {activeList === lid && (
                                    <div className="px-5 pb-5 bg-muted/20">
                                        <ListEditor list={listItems} schema={ldef.schema} onChange={setListItems} />
                                        <div className="mt-4 flex justify-end gap-2">
                                            <button onClick={() => setActiveList(null)} className="px-4 py-2 text-sm text-muted-foreground hover:text-primary">Cancelar</button>
                                            <button onClick={saveList} disabled={savingKey === "list"}
                                                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-bold text-white hover:bg-primary/90 disabled:opacity-50">
                                                <Save className="h-3.5 w-3.5" /> Guardar lista
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------- Sub-components ----------
function SettingRow({ k, conf, value, onSave, saving }) {
    const [v, setV] = useState(value);
    useEffect(() => setV(value), [value]);
    const dirty = v !== value;
    return (
        <div data-testid={`setting-${k}`}>
            <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-primary/80">{conf.label}</label>
                {dirty && (
                    <button onClick={() => onSave(v)} disabled={saving}
                        className="inline-flex items-center gap-1 rounded bg-primary px-2 py-0.5 text-[10px] font-bold text-white hover:bg-primary/90 disabled:opacity-50">
                        <Save className="h-3 w-3" /> {saving ? "..." : "Guardar"}
                    </button>
                )}
            </div>
            <FieldInput kind={conf.kind} value={v} onChange={setV} />
        </div>
    );
}

function FieldRow({ f, value, onSave, saving }) {
    const [v, setV] = useState(value);
    useEffect(() => setV(value), [value]);
    const dirty = v !== value;
    return (
        <div>
            <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-primary/80">{f.label}</label>
                {dirty && (
                    <button onClick={() => onSave(v)} disabled={saving} data-testid="field-save-btn"
                        className="inline-flex items-center gap-1 rounded bg-primary px-2 py-0.5 text-[10px] font-bold text-white hover:bg-primary/90 disabled:opacity-50">
                        <Save className="h-3 w-3" /> {saving ? "..." : "Guardar"}
                    </button>
                )}
            </div>
            <FieldInput kind={f.kind} value={v} onChange={setV} />
        </div>
    );
}
